/**
 * UTC Period Calculations for Customer Billing
 * 
 * Provides calendar-based period boundaries using UTC timezone
 * Supports forecasting for current (partial) periods
 */

export interface PeriodData {
  type: 'current-month' | 'previous-month' | 'current-week' | 'previous-week' | 'today' | 'yesterday';
  startDate: Date; // UTC
  endDate: Date;   // UTC
  actualEndDate?: Date; // For current periods, where we have actual data
  isPartial: boolean; // True for current periods
  daysElapsed: number; // Days with actual data
  totalDays: number; // Total days in period
  label: string; // Human-readable label
}

export interface ForecastData {
  projectedCost: number;
  projectedCalls: number;
  confidenceLevel: number; // 0-1 scale
  methodology: 'daily-average' | 'weekly-average';
  basedOnDays: number; // Number of days used for calculation
}

/**
 * Calculate period boundaries for billing analysis
 * All dates are in UTC timezone
 */
export function calculatePeriodBounds(periodType: string): PeriodData {
  const now = new Date(); // Current UTC time
  
  console.log(`🔍 [Period Debug] Calculating period bounds for: ${periodType}`);
  console.log(`🔍 [Period Debug] Current UTC time: ${now.toISOString()}`);
  console.log(`🔍 [Period Debug] Current local time: ${now.toString()}`);
  console.log(`🔍 [Period Debug] Timezone offset (minutes): ${now.getTimezoneOffset()}`);
  console.log(`🔍 [Period Debug] UTC Year: ${now.getUTCFullYear()}, Month: ${now.getUTCMonth()}, Date: ${now.getUTCDate()}`);
  
  switch (periodType) {
    case 'current-month':
      return calculateCurrentMonth(now);
    case 'previous-month':
      return calculatePreviousMonth(now);
    case 'current-week':
      return calculateCurrentWeek(now);
    case 'previous-week':
      return calculatePreviousWeek(now);
    case 'today':
      return calculateToday(now);
    case 'yesterday':
      return calculateYesterday(now);
    default:
      throw new Error(`Unsupported period type: ${periodType}`);
  }
}

/**
 * Current month: From 1st of month 00:00:00 UTC to end of month 23:59:59 UTC
 * Partial period with forecasting capability
 */
function calculateCurrentMonth(now: Date): PeriodData {
  const startDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0, 0));
  const endDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0, 23, 59, 59, 999));
  
  const daysInMonth = endDate.getUTCDate();
  const daysElapsed = now.getUTCDate();
  
  return {
    type: 'current-month',
    startDate,
    endDate,
    actualEndDate: now,
    isPartial: true,
    daysElapsed,
    totalDays: daysInMonth,
    label: `Current Month (${now.toLocaleString('default', { month: 'long', year: 'numeric' })})`
  };
}

/**
 * Previous month: Complete previous month in UTC
 */
function calculatePreviousMonth(now: Date): PeriodData {
  const startDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1, 0, 0, 0, 0));
  const endDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0, 23, 59, 59, 999));
  
  const daysInMonth = endDate.getUTCDate();
  
  const prevMonthDate = new Date(now);
  prevMonthDate.setUTCMonth(prevMonthDate.getUTCMonth() - 1);
  
  return {
    type: 'previous-month',
    startDate,
    endDate,
    isPartial: false,
    daysElapsed: daysInMonth,
    totalDays: daysInMonth,
    label: `Previous Month (${prevMonthDate.toLocaleString('default', { month: 'long', year: 'numeric' })})`
  };
}

/**
 * Current week: Monday 00:00:00 UTC to Sunday 23:59:59 UTC
 * Partial period with forecasting capability
 */
function calculateCurrentWeek(now: Date): PeriodData {
  const currentDay = now.getUTCDay(); // 0 = Sunday, 1 = Monday, etc.
  const mondayOffset = currentDay === 0 ? -6 : -(currentDay - 1); // Calculate days back to Monday
  
  const startDate = new Date(now);
  startDate.setUTCDate(now.getUTCDate() + mondayOffset);
  startDate.setUTCHours(0, 0, 0, 0);
  
  const endDate = new Date(startDate);
  endDate.setUTCDate(startDate.getUTCDate() + 6);
  endDate.setUTCHours(23, 59, 59, 999);
  
  // Calculate days elapsed (Monday = 1, Tuesday = 2, etc.)
  const daysElapsed = currentDay === 0 ? 7 : currentDay; // Sunday counts as day 7
  
  const result = {
    type: 'current-week' as const,
    startDate,
    endDate,
    actualEndDate: now,
    isPartial: true,
    daysElapsed,
    totalDays: 7,
    label: `Current Week (${formatDateRange(startDate, endDate)})`
  };
  
  console.log(`🔍 [Week Debug] Current UTC day of week: ${currentDay} (0=Sunday, 1=Monday...)`);
  console.log(`🔍 [Week Debug] Monday offset: ${mondayOffset}`);
  console.log(`🔍 [Week Debug] Week start (UTC): ${startDate.toISOString()}`);
  console.log(`🔍 [Week Debug] Week end (UTC): ${endDate.toISOString()}`);
  console.log(`🔍 [Week Debug] Actual end (now): ${now.toISOString()}`);
  console.log(`🔍 [Week Debug] Days elapsed: ${daysElapsed}`);
  
  return result;
}

/**
 * Previous week: Complete previous Monday-Sunday in UTC
 */
function calculatePreviousWeek(now: Date): PeriodData {
  const currentWeek = calculateCurrentWeek(now);
  
  const startDate = new Date(currentWeek.startDate);
  startDate.setUTCDate(startDate.getUTCDate() - 7);
  
  const endDate = new Date(currentWeek.startDate);
  endDate.setUTCDate(endDate.getUTCDate() - 1);
  endDate.setUTCHours(23, 59, 59, 999);
  
  return {
    type: 'previous-week',
    startDate,
    endDate,
    isPartial: false,
    daysElapsed: 7,
    totalDays: 7,
    label: `Previous Week (${formatDateRange(startDate, endDate)})`
  };
}

/**
 * Today: Current day from 00:00:00 UTC to 23:59:59 UTC
 * Partial period with forecasting capability
 */
function calculateToday(now: Date): PeriodData {
  const startDate = new Date(Date.UTC(
    now.getUTCFullYear(), 
    now.getUTCMonth(), 
    now.getUTCDate(), 
    0, 0, 0, 0
  ));
  
  const endDate = new Date(Date.UTC(
    now.getUTCFullYear(), 
    now.getUTCMonth(), 
    now.getUTCDate(), 
    23, 59, 59, 999
  ));
  
  // Calculate fractional day elapsed (for hourly forecasting)
  const hoursElapsed = now.getUTCHours() + (now.getUTCMinutes() / 60);
  const dayFraction = hoursElapsed / 24;
  
  const result = {
    type: 'today' as const,
    startDate,
    endDate,
    actualEndDate: now,
    isPartial: true,
    daysElapsed: dayFraction, // Fractional for intraday forecasting
    totalDays: 1,
    label: `Today (${now.toLocaleDateString()})`
  };
  
  console.log(`🔍 [Today Debug] Today start (UTC): ${startDate.toISOString()}`);
  console.log(`🔍 [Today Debug] Today end (UTC): ${endDate.toISOString()}`);
  console.log(`🔍 [Today Debug] Current time (UTC): ${now.toISOString()}`);
  console.log(`🔍 [Today Debug] Hours elapsed: ${hoursElapsed.toFixed(2)}`);
  console.log(`🔍 [Today Debug] Day fraction: ${dayFraction.toFixed(3)}`);
  
  return result;
}

/**
 * Yesterday: Complete previous day in UTC
 */
function calculateYesterday(now: Date): PeriodData {
  const startDate = new Date(Date.UTC(
    now.getUTCFullYear(), 
    now.getUTCMonth(), 
    now.getUTCDate() - 1, 
    0, 0, 0, 0
  ));
  
  const endDate = new Date(Date.UTC(
    now.getUTCFullYear(), 
    now.getUTCMonth(), 
    now.getUTCDate() - 1, 
    23, 59, 59, 999
  ));
  
  return {
    type: 'yesterday',
    startDate,
    endDate,
    isPartial: false,
    daysElapsed: 1,
    totalDays: 1,
    label: `Yesterday (${startDate.toLocaleDateString()})`
  };
}

/**
 * Check if a period has sufficient data for reliable forecasting
 * Minimum 5 days for weekly/monthly periods
 */
export function hasReliableForecastData(period: PeriodData): boolean {
  if (!period.isPartial) {
    return false; // Complete periods don't need forecasting
  }
  
  switch (period.type) {
    case 'current-month':
    case 'current-week':
      return period.daysElapsed >= 5;
    case 'today':
      return period.daysElapsed >= 0.2; // At least ~5 hours of data
    default:
      return false;
  }
}

/**
 * Check if weekends should be excluded from forecasting
 * Used for business-focused forecasting models
 */
export function shouldExcludeWeekends(period: PeriodData): boolean {
  // For weekly and monthly periods, we might want to exclude weekends
  // from forecasting if business usage patterns are very different
  return period.type === 'current-week' || period.type === 'current-month';
}

/**
 * Get business days in a period (excluding weekends)
 * Used for weekend-excluded forecasting
 */
export function getBusinessDays(startDate: Date, endDate: Date): number {
  let businessDays = 0;
  const currentDate = new Date(startDate);
  
  while (currentDate <= endDate) {
    const dayOfWeek = currentDate.getUTCDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Not Sunday (0) or Saturday (6)
      businessDays++;
    }
    currentDate.setUTCDate(currentDate.getUTCDate() + 1);
  }
  
  return businessDays;
}

/**
 * Calculate business days elapsed in current period
 * Used for weekend-excluded forecasting
 */
export function getBusinessDaysElapsed(period: PeriodData): number {
  if (!period.isPartial || !period.actualEndDate) {
    return period.daysElapsed;
  }
  
  return getBusinessDays(period.startDate, period.actualEndDate);
}

/**
 * Helper function to format date ranges for labels
 */
function formatDateRange(startDate: Date, endDate: Date): string {
  const start = startDate.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric' 
  });
  const end = endDate.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric' 
  });
  
  return `${start} - ${end}`;
}

/**
 * Validate that a period is properly configured
 */
export function validatePeriod(period: PeriodData): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (period.startDate >= period.endDate) {
    errors.push('Start date must be before end date');
  }
  
  if (period.isPartial && !period.actualEndDate) {
    errors.push('Partial periods must have actualEndDate');
  }
  
  if (period.daysElapsed < 0 || period.daysElapsed > period.totalDays) {
    errors.push('Days elapsed must be between 0 and total days');
  }
  
  if (period.totalDays <= 0) {
    errors.push('Total days must be positive');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Get default period type for billing view
 */
export function getDefaultPeriodType(): string {
  return 'current-week';
}

/**
 * Get all available period types for UI selector
 */
export function getAvailablePeriodTypes(): Array<{ value: string; label: string }> {
  const now = new Date();
  
  return [
    { value: 'current-month', label: calculateCurrentMonth(now).label },
    { value: 'previous-month', label: calculatePreviousMonth(now).label },
    { value: 'current-week', label: calculateCurrentWeek(now).label },
    { value: 'previous-week', label: calculatePreviousWeek(now).label },
    { value: 'today', label: calculateToday(now).label },
    { value: 'yesterday', label: calculateYesterday(now).label }
  ];
}
